// Tính toán cho bảng kê hóa đơn điều chỉnh / thay thế.
// Tách riêng khỏi helper.js vì đây là nghiệp vụ của bước 2.

// Backend trả số dạng chuỗi ("25005840.0000"), ô nhập cũng là chuỗi
export const toNumber = value => {
  if (value === null || value === undefined || value === "") return 0;
  const num = Number(String(value).replace(/,/g, ""));
  return Number.isNaN(num) ? 0 : num;
};

// "10%" -> 0.1 ; "KCT"/"KKKNT"/rỗng -> 0
export const parseTaxRate = value => {
  if (value === null || value === undefined) return 0;
  const matched = String(value).match(/-?\d+(\.\d+)?/);
  if (!matched) return 0;
  return toNumber(matched[0]) / 100;
};

// Người dùng chỉ nhập số lượng / đơn giá / thuế suất, ba cột tiền tự tính
export const recalcAfter = after => {
  const sluong = toNumber(after.sluong);
  const dgia = toNumber(after.dgia);
  const thtien = sluong * dgia;
  const tthue = thtien * parseTaxRate(after.tsuat);
  return {
    ...after,
    thtien: String(thtien),
    tthue: String(tthue),
    tong: String(thtien + tthue),
  };
};

// Dòng chỉ được tính chênh lệch khi người dùng đã thực sự nhập gì đó
export const isAfterFilled = after => {
  if (!after) return false;
  return ["thhdvu", "sluong", "dgia", "tsuat"].some(
    key => after[key] !== null && after[key] !== undefined && after[key] !== ""
  );
};

export const computeDiff = (before, after) => {
  if (!isAfterFilled(after)) {
    return { net: null, vat: null, pay: null };
  }
  return {
    net: toNumber(after.thtien) - toNumber(before.thtien),
    vat: toNumber(after.tthue) - toNumber(before.tthue),
    pay: toNumber(after.tong) - toNumber(before.tong),
  };
};

// Tên hàng hóa bắt buộc nhập khi dòng đó đang được điều chỉnh
export const rowErrors = row => {
  if (!isAfterFilled(row.after)) return {};
  return !row.after.thhdvu ? { thhdvu: "Bắt buộc nhập" } : {};
};

export const hasBlockingError = rows =>
  rows.some(row => Object.keys(rowErrors(row)).length > 0);

export const sumRows = rows =>
  rows.reduce(
    (acc, row) => {
      const diff = computeDiff(row.before, row.after);
      return {
        afterTong: acc.afterTong + toNumber(row.after?.tong),
        diffNet: acc.diffNet + (diff.net || 0),
        diffVat: acc.diffVat + (diff.vat || 0),
        diffPay: acc.diffPay + (diff.pay || 0),
      };
    },
    { afterTong: 0, diffNet: 0, diffVat: 0, diffPay: 0 }
  );

// Số hóa đơn gốc, dùng cho dòng "Tổng cộng N hóa đơn"
export const countRootInvoices = rows =>
  new Set(rows.map(row => row.root_id)).size;

export const buildInitPayload = (filters, selectedRows) => ({
  type: filters?.type,
  month: filters?.month,
  invoice_ids: selectedRows.map(row => row.id),
});

// Payload lưu dùng đúng bộ tên field như response chi tiết, để hai chiều
// gửi/nhận không phải ánh xạ qua lại. Chỉ ép kiểu các trường số; trường nào
// nguồn không có thì giữ null chứ không bịa ra 0.
const numOrNull = value =>
  value === null || value === undefined || value === ""
    ? null
    : toNumber(value);

const buildSaveRow = (row, index, invoice) => {
  const before = row.before || {};
  const after = row.after || {};
  const diff = computeDiff(before, after);
  const lineReason = row.line_reason || "";

  return {
    detail_id: row.detail_id,
    root_id: row.root_id,
    stt: row.stt ?? index + 1,
    before: {
      ttchung_khmshdon: before.ttchung_khmshdon ?? null,
      ttchung_khhdon: before.ttchung_khhdon ?? null,
      ttchung_shdon: before.ttchung_shdon ?? null,
      ttchung_nlap: before.ttchung_nlap ?? null,
      thhdvu: before.thhdvu ?? null,
      sluong: numOrNull(before.sluong),
      dgia: numOrNull(before.dgia),
      thtien: numOrNull(before.thtien),
      tsuat: before.tsuat ?? null,
      tthue: numOrNull(before.tthue),
      tong: numOrNull(before.tong),
      ghichu: lineReason,
    },
    after: {
      ttchung_khmshdon: after.ttchung_khmshdon || invoice?.khmshdon || null,
      ttchung_khhdon: after.ttchung_khhdon || invoice?.khhdon || null,
      ttchung_shdon: after.ttchung_shdon || invoice?.shdon || null,
      thhdvu: after.thhdvu ?? null,
      sluong: numOrNull(after.sluong),
      dgia: numOrNull(after.dgia),
      thtien: numOrNull(after.thtien),
      tsuat: after.tsuat ?? null,
      tthue: numOrNull(after.tthue),
      tong: numOrNull(after.tong),
    },
    diff: {
      net: diff.net,
      vat: diff.vat,
      pay: diff.pay,
    },
    line_reason: lineReason,
  };
};

export const buildSavePayload = (
  detail,
  rows,
  reason,
  statementId,
  buyerEmail
) => ({
  // Bắt buộc để backend biết lưu vào bảng kê nào. Ưu tiên id đang mở trên màn
  // hình, phòng khi detail chưa kịp về.
  statement_id: statementId ?? detail?.statement_id,
  adjust_mode: detail?.adjust_mode,
  period: detail?.period,
  seller: {
    name: detail?.seller?.name || "",
    tax_code: detail?.seller?.tax_code || "",
    address: detail?.seller?.address || "",
  },
  buyer: {
    name: detail?.buyer?.name || "",
    tax_code: detail?.buyer?.tax_code || "",
    address: detail?.buyer?.address || "",
    // Email nhận link ký, đặt trong buyer cho khớp response chi tiết.
    // Chỉ có khi bấm gửi duyệt; bấm Lưu thường thì giữ email cũ của BE.
    ...(buyerEmail ? { esign_email: buyerEmail } : {}),
  },
  reason: reason || "",
  rows: rows.map((row, index) => buildSaveRow(row, index, detail?.invoice)),
});

// Backend trả số kèm đuôi 0 thừa ("1.0000", "42180000.0000"), bỏ phần thập
// phân rỗng đi. Vẫn giữ thập phân thật nếu có, ví dụ "1.5000" -> "1.5".
export const stripDecimalZeros = value => {
  if (value === null || value === undefined || value === "") return "";
  const num = toNumber(value);
  if (Number.isNaN(num)) return "";
  return String(Number(num.toFixed(4)));
};

export const formatQuantity = stripDecimalZeros;

// Dữ liệu API còn ở dạng thô ("1.0000"), chuẩn hóa ngay khi nạp để cả state
// lẫn ô nhập đều sạch, không chỉ đẹp lúc mất focus
// Số lượng và đơn giá không có phần thập phân, chỉ giữ chữ số
export const onlyDigits = value => String(value ?? "").replace(/\D/g, "");

const toIntegerString = value =>
  value === null || value === undefined || value === ""
    ? value
    : String(Math.round(toNumber(value)));

export const normalizeRowsForEdit = rows =>
  (rows || []).map(row => {
    const after = row.after || {};
    return {
      ...row,
      after: {
        ...after,
        sluong: toIntegerString(after.sluong),
        dgia: toIntegerString(after.dgia),
      },
    };
  });

// API trả HTTP 200 kèm { result: false, message } khi nghiệp vụ không hợp lệ.
// Dùng chung cho mọi chỗ gọi save / submit / approve / reject.
export const isFailedResponse = response => response?.result === false;

export const readResponseMessage = (response, fallback) =>
  response?.message || fallback;
